import { Card, ModernCardHeader } from "../../../components/cards";
import { useMe } from "../../users/hooks/useMe";
import { Activity, ShoppingCart, Truck } from "lucide-react";
import { TasksWidget } from "../../tasks/components";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { useNavigate } from "react-router-dom";
import { useOrders } from "../../orders/hooks";
import { formatDisplayDate } from "../../../helpers/date";
import { QuickPickupScanWidget, GivenCansShortcut } from "../components";

export const Customer = () => {
  const { data } = useMe();
  const navigate = useNavigate();
  const { data: ordersData, loading: loadingOrders } = useOrders({
    where: data?.me?.id ? { salesRepId: data.me.id } : {},
  });

  const myOrders = [...(ordersData?.orders?.results ?? [])]
    .sort((a, b) => b.id - a.id)
    .slice(0, 5);

  const myDeliveries = (ordersData?.orders?.results ?? [])
    .filter((order) => ["ToPlan", "Planned", "InProgress"].includes(order.deliveryStatus))
    .slice(0, 5);

  return (
    <div className="space-y-4 p-2">
      {/* Welcome Banner */}
      <Card
        padding="md"
        className="bg-gradient-to-r from-primary-600 to-primary-700 border-0 text-white"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold mb-1">
              Bienvenue, {data?.me?.firstname ?? ""}
            </h2>
            <p className="text-sm text-white/90">
              Suivez vos tâches, vos commandes et vos livraisons
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QuickPickupScanWidget />
        <GivenCansShortcut />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <ModernCardHeader
            title="Mes commandes"
            subtitle="Dernières commandes passées"
            action={
              <span
                className="text-xs text-indigo-600 cursor-pointer hover:underline"
                onClick={() => navigate("/orders")}
              >
                Voir tout
              </span>
            }
          />
          {loadingOrders ? (
            <Loading />
          ) : !myOrders.length ? (
            <EmptyList text="Aucune commande" />
          ) : (
            <div className="space-y-1 mt-3">
              {myOrders.map((order) => (
                <div
                  key={`my-order-${order.id}`}
                  className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200"
                  onClick={() => navigate(`/order/${order.id}`)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-indigo-50 text-indigo-600 p-1.5 rounded-md flex-shrink-0">
                      <ShoppingCart className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        {order.number ?? `#${order.id}`}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{order.customer?.name}</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                    {((order.totalTtcCents ?? 0) / 100).toFixed(2)} €
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <ModernCardHeader title="Livraisons à venir" subtitle="Mes commandes à livrer" />
          {loadingOrders ? (
            <Loading />
          ) : !myDeliveries.length ? (
            <EmptyList text="Aucune livraison en attente" />
          ) : (
            <div className="space-y-1 mt-3">
              {myDeliveries.map((order) => (
                <div
                  key={`my-delivery-${order.id}`}
                  className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200"
                  onClick={() => navigate(`/order/${order.id}`)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-orange-50 text-orange-600 p-1.5 rounded-md flex-shrink-0">
                      <Truck className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        {order.number ?? `#${order.id}`} — {order.customer?.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {order.plannedDeliveryAt
                          ? formatDisplayDate(order.plannedDeliveryAt)
                          : "Date non planifiée"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {data?.me?.id && <TasksWidget userId={data.me.id} />}
    </div>
  );
};
